import { isAbortError, isTimeoutError, useAbortableRequest } from "hooks/useAbortableRequest";
import { useReducer } from "react";
import { useTranslate } from "react-admin";
import { useNotifyError } from "utils/notifiers/useNotifyError";

import { PALLETS_SAVE_TIMEOUT_MS, PDF_EXTRACTION_TIMEOUT_MS } from "./pdfExtractionTimeouts";

const withRowIds = (pallets) => (pallets || []).map((pallet, index) => ({ ...pallet, id: index + 1 }));

const initialState = (preview) => ({
	extraction: preview,
	rows: withRowIds(preview?.pallets),
	hasEdits: false,
	pending: null,
});

const reducer = (state, action) => {
	switch (action.type) {
		case "cell_changed":
			return {
				...state,
				hasEdits: true,
				rows: state.rows.map((row) =>
					row.id === action.id ? { ...row, [action.source]: action.value } : row,
				),
			};
		case "weight_unit_changed":
			return {
				...state,
				hasEdits: true,
				extraction: { ...state.extraction, weight_unit: action.value },
			};
		case "reparse_started":
			return { ...state, pending: "reparse" };
		case "reparse_succeeded":
			return {
				extraction: action.extraction,
				rows: withRowIds(action.extraction?.pallets),
				hasEdits: false,
				pending: null,
			};
		case "save_started":
			return { ...state, pending: "save" };
		case "request_settled":
			return { ...state, pending: null };
		default:
			return state;
	}
};

export const usePdfExtractionPreview = ({ order, preview, onSaved }) => {
	const translate = useTranslate();
	const notifyError = useNotifyError();
	const request = useAbortableRequest();

	const [state, dispatch] = useReducer(reducer, preview, initialState);

	const notifyRequestError = (error, timeoutMessageKey) => {
		if (isTimeoutError(error)) {
			notifyError(translate(timeoutMessageKey));
		} else if (!isAbortError(error)) {
			notifyError(error);
		}
	};

	const changeCell = (id, source, value) => dispatch({ type: "cell_changed", id, source, value });

	const changeWeightUnit = (value) => dispatch({ type: "weight_unit_changed", value });

	const reparse = async (clarification) => {
		dispatch({ type: "reparse_started" });
		try {
			const { json } = await request(`/orders/${order.id}/pallets-from-pdf/preview`, {
				method: "POST",
				body: JSON.stringify({ clarification }),
				timeoutMs: PDF_EXTRACTION_TIMEOUT_MS,
			});
			dispatch({ type: "reparse_succeeded", extraction: json });
		} catch (error) {
			notifyRequestError(error, "osfc.message.warn.pdf_extraction_timed_out");
			dispatch({ type: "request_settled" });
		}
	};

	const save = async () => {
		dispatch({ type: "save_started" });
		try {
			const pallets = state.rows.map(({ id, unit_weights, ...pallet }) => pallet);
			await request(`/orders/${order.id}/generate-pallets-from-pdf`, {
				method: "POST",
				body: JSON.stringify({ pallets }),
				timeoutMs: PALLETS_SAVE_TIMEOUT_MS,
			});
			dispatch({ type: "request_settled" });
			onSaved();
		} catch (error) {
			notifyRequestError(error, "osfc.message.warn.pallets_save_timed_out");
			dispatch({ type: "request_settled" });
		}
	};

	return {
		extraction: state.extraction,
		rows: state.rows,
		hasEdits: state.hasEdits,
		isReparsing: state.pending === "reparse",
		isSaving: state.pending === "save",
		isBusy: state.pending !== null,
		changeCell,
		changeWeightUnit,
		reparse,
		save,
	};
};
